<template>
  <div class="row mt15 mb15 flex" :class="{ 'center-xs': selectedAddress, 'local-center-select': !selectedAddress }">
    <div class="select-wrapper relative" @click="!selectedAddress ? onTogglePopup() : null">
      <custom-select
        name="addresses_select"
        id="addresses_select"
        :selected-id="selectedAddress"
        :options="addressesSelectOptions"
        :class="{ 'cl-tertiary': addresses.length === 0, 'empty': !selectedAddress, 'not-address-select': !selectedAddress }"
        :disabled="disableSelect || !addresses.length"
        :actions="!currentUser ? [{
          label: $t('Log in to add more addresses'),
          action: onOpenLoginModal
        }] : null"
        :placeholder="$t('Enter your address')"
        @change="(addressId) => switchLocation({ code: addressId, forceCategoriesLoad: true })"
      />
      <label for="addresses_select" class="select pointer select-wrapper" :class="{ 'address-label': !selectedAddress }"/>
      <address-map-popup
        :open="openPopup"
        :clearInput="clearPopupInput"
        @toggle="onTogglePopup"
        @openMapModal="openMapModal"
        @openAddressConfirmationModal="openAddressConfirmationModal"
        @selectAddress="selectAddress"
      />
    </div>
    <address-map-modal
      :open-modal="showMapModal"
      @address-selected="(address) => setSelectedAddressData(address)"
      @close="closeMapModal"
      @handleCancelAction="setSelectedAddressData"
    />
    <address-confirmation-modal
      :open-modal="showAddressConfirmationModal"
      :current-address="stateSelectedAddress || {}"
      @addressUpdated="setSelectedAddressData"
      @selectAddress="selectAddress"
      @close="() => {
        setSelectedAddressData(null);
        showAddressConfirmationModal = false;
      }"
    />
  </div>
</template>

<script async>
import { mapGetters, mapActions, mapState } from 'vuex'
import AddressMapPopup from 'theme/components/core/blocks/MyAccount/ShippingDetails/AddressMapPopup.vue';
import AddressConfirmationModal from 'theme/components/core/blocks/MyAccount/ShippingDetails/AddressConfirmationModal.vue';
import CustomSelect from 'theme/components/core/blocks/Form/CustomSelect.vue';
import { v4 as uuidv4 } from 'uuid';
import i18n from '@vue-storefront/i18n';
import resolveCity from '@vue-storefront/core/scripts/utils/resolve-city';

const AddressMapModal = () => import(/* webpackChunkName: "vsf-address-map-modal" */ 'theme/components/core/blocks/MyAccount/ShippingDetails/AddressMapModal.vue');

export default {
  name: 'AddressSelect',
  components: {
    CustomSelect,
    AddressMapModal,
    AddressMapPopup,
    AddressConfirmationModal
  },
  async mounted () {
    this.disableSelect = false

    if (!this.addresses || !this.addresses.length) {
      if (this.currentUser) {
        await this.loadAddressList()
          .then(() => {
            console.log('Addresses: ', this.addresses.length);
            if (!this.addresses.length) {
              this.openPopup = true;
            }
          });
      } else {
        await this.loadAddresses()
          .then(() => {
            console.log('Addresses: ', this.addresses.length);
            if (!this.addresses.length) {
              this.openPopup = true;
            }
          });
      }
    }
  },
  data () {
    return {
      disableSelect: true,
      openPopup: false,
      showMapModal: false,
      showAddressConfirmationModal: false,
      selectedAddressData: null,
      clearPopupInput: false
    }
  },
  props: {
    routeSuffix: {
      type: String,
      default: () => ''
    }
  },
  computed: {
    ...mapState({
      currentUser: (state) => state.user.current,
      stateSelectedAddress: (state) => state.address.selectedAddress
    }),
    ...mapGetters({
      customerAddresses: 'address/getAddressList',
      customerDefaultAddress: 'address/getDefaultAddress',
      getGuestAddresses: 'address/getGuestAddresses'
    }),
    addresses () {
      if (this.customerAddresses && this.customerAddresses.length) {
        return this.customerAddresses
      }

      return this.getGuestAddresses
    },
    selectedAddress () {
      if (!this.customerDefaultAddress || typeof this.customerDefaultAddress === 'undefined' || typeof this.customerDefaultAddress === undefined) {
        if (this.customerAddresses && this.customerAddresses.length) {
          let defaultAddress = this.customerAddresses.find(address => address.default_shipping)
          if (defaultAddress) {
            return defaultAddress.id
          }
          return this.customerAddresses[0].id
        }

        if (this.getGuestAddresses && this.getGuestAddresses.length) {
          const defaultAddress = this.getGuestAddresses.find(address => address.default_shipping)
          if (defaultAddress) {
            return defaultAddress.id
          }
          return this.getGuestAddresses[0].id
        }

        return ''
      }

      return this.customerDefaultAddress.id
    },
    addressesSelectOptions () {
      if (!this.addresses.length) {
        return [
          {
            id: '',
            label: this.$t('Enter your address'),
            selected: true
          }
        ];
      }
      return this.addresses.map(address => ({
        id: address.id,
        label: [address.city, address.street, address.house].filter(Boolean).join(', '),
        action: {
          icon: 'close',
          action: this.removeAddress
        },
        selected: address.id === this.selectedAddress
      }));
    }
  },
  methods: {
    ...mapActions({
      loadAddresses: 'address/loadGuestList',
      addGuestAddress: 'address/addGuestAddress',
      deleteGuestAddress: 'address/deleteGuestAddress',
      makeDefault: 'address/makeDefault',
      loadTags: 'partner_tags/loadTags',
      updateShippingDetailsState: 'shipping/updateShippingDetailsState',
      loadAddressList: 'address/loadList',
      removeCustomerAddress: 'address/delete',
      addCustomerAddress: 'address/create',
      setSelectedAddress: 'address/setSelectedAddress'
    }),
    setSelectedAddressData (address) {
      this.selectedAddressData = address;
      this.setSelectedAddress(address);
    },
    selectAddress (address) {
      if (this.currentUser) {
        this.addCustomerAddress({
          address: {
            city: resolveCity(address.address),
            country: address.address.country,
            country_code: address.address.country_code,
            state: address.address.state,
            street: address.address.road,
            house: address.address.house_number,
            postcode: address.address.postcode,
            api_details: JSON.stringify(address),
            latitude: address.lat,
            longitude: address.lon,
            default_shipping: !(this.addresses && this.addresses.length),
            customer_id: this.currentUser.id,
            firstname: this.currentUser.firstname,
            lastname: this.currentUser.lastname,
            phone_number: this.currentUser.telephone
          }
        }).then(async res => {
          this.$store.dispatch('notification/spawnNotification', {
            type: 'success',
            message: i18n.t('Your address is added!'),
            action1: {label: i18n.t('OK')}
          })
          if (res.id && res.default_shipping) {
            this.makeDefault({address_id: res.id})
              .then(defaultAddress => {
                this.$bus.$emit('checkout-after-shippingDetails', defaultAddress);
                this.updateShippingDetailsState({
                  shipToMyAddress: true,
                  showNewAddressForm: false
                })
              })
          }
        }).then(() => {
          this.openPopup = false;
          this.showAddressConfirmationModal = false;
          this.$bus.$on('popup-hide', 'address-map-popup');
          this.$bus.$emit('modal-hide', 'modal-address-confirmation');
        })
          .catch(err => {
            this.$store.dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t('Error has happened during adding the new address! Please, try again or contact the support!'),
              action1: {label: i18n.t('OK')}
            })
          })
      } else {
        this.addGuestAddress({
          id: uuidv4(),
          default_shipping: !(this.addresses && this.addresses.length),
          city: resolveCity(address.address),
          country: address.address.country,
          country_code: address.address.country_code,
          state: address.address.state,
          street: address.address.road,
          house: address.address.house_number,
          postcode: address.address.postcode,
          api_details: JSON.stringify(address),
          latitude: address.lat,
          longitude: address.lon
        })
          .then(() => {
            this.openPopup = false;
            this.showAddressConfirmationModal = false;
            this.$bus.$on('popup-hide', 'address-map-popup');
            this.$bus.$emit('modal-hide', 'modal-address-confirmation');
          })
      }
    },
    async switchLocation (payload) {
      if (!this.currentUser) return;
      // switch address to default
      const selectedAddress = this.addresses.find(address => address.id === payload.code);
      if (selectedAddress) {
        this.makeDefault({address_id: selectedAddress.id})
          .then((res) => {
            this.$store.dispatch('notification/spawnNotification', {
              type: 'success',
              message: i18n.t('Your default address is updated!'),
              action1: {label: i18n.t('OK')}
            })
            this.$bus.$emit('checkout-after-shippingDetails', res);
            this.$bus.$emit('address-list-address-changed', res);
            this.updateShippingDetailsState({
              shipToMyAddress: true,
              showNewAddressForm: false
            })
          })
          .catch(() => {
            this.$store.dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t('Error is happened during making address default!'),
              action1: {label: i18n.t('OK')}
            })
          })
      } else {
        await this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: i18n.t('Something went wrong ...'),
          action1: {label: i18n.t('OK')}
        });
      }
    },
    onTogglePopup (closeByDefault = false) {
      if (typeof closeByDefault === 'boolean' && closeByDefault) {
        this.openPopup = false;
        this.setSelectedAddress(null);
        return;
      }
      this.openPopup = !this.openPopup;
    },
    closeMapModal () {
      this.showMapModal = false;
    },
    openMapModal () {
      this.showMapModal = true;
      this.$bus.$emit('modal-toggle', 'modal-address-map');
    },
    openAddressConfirmationModal () {
      this.showAddressConfirmationModal = true;
      this.$bus.$emit('modal-toggle', 'modal-address-confirmation');
    },
    onOpenLoginModal () {
      this.$emit('toggle', true);
      this.$store.commit('ui/setAuthElem', 'login');
      this.$bus.$emit('modal-show', 'modal-signup');
    },
    async removeAddress (addressId) {
      if (!addressId) {
        await this.$store.dispatch('notification/spawnNotification', {
          type: 'error',
          message: i18n.t('Something went wrong ...'),
          action1: {label: i18n.t('OK')}
        });
        return;
      }
      if (this.currentUser) {
        this.removeCustomerAddress({address_id: addressId})
          .then(() => {
            this.$store.dispatch('notification/spawnNotification', {
              type: 'success',
              message: i18n.t('Your address is removed!'),
              action1: {label: i18n.t('OK')}
            })
          })
          .catch(() => {
            this.$store.dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t('Error is happened during removing the address!'),
              action1: {label: i18n.t('OK')}
            })
          })
      } else {
        await this.deleteGuestAddress(addressId)
          .then(response => {
            if (!response.length) {
              this.openPopup = true;
              this.updateShippingDetailsState({
                shipToMyAddress: false,
                showNewAddressForm: true
              })
              this.$bus.$emit('checkout-after-shippingDetails', {});
            }
          })
          .catch(error => {
            console.error(error);
            this.$store.dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t('Something went wrong ...'),
              action1: {label: i18n.t('OK')}
            })
          });
      }
    }
  },
  beforeMount () {
    this.$bus.$on('handle-toggle-address-popup', this.onTogglePopup);
  },
  beforeDestroy () {
    this.$bus.$off('handle-toggle-address-popup', this.onTogglePopup);
  },
  watch: {
    showMapModal (value) {
      this.clearPopupInput = !!(!value && !this.selectedAddressData);

      if (!value) {
        this.selectedAddressData = null;
      }
    }
  }
}
</script>
<style lang="scss" scoped>
@import '~theme/css/variables/colors';
@import '~theme/css/helpers/functions/color';
@import '~theme/css/base/text';
@import '~theme/css/layout/screen';

$color-tertiary: color(tertiary);
$color-black: color(black);
$color-puerto-rico: color(puerto-rico);
$color-hover: color(tertiary, $colors-background);

.row {
  margin-top: 20px;
}

.select-wrapper {
  padding-left: 16px;
  cursor: pointer;
  position: relative;
  z-index: 10;

  @media only screen and (max-width: $screen-md) {
    width: 24px;
  }

  @media only screen and (max-width: $screen-sm-l) {
    margin-right: 24px;
  }

  label {
    color: #999;
    position: absolute;
    pointer-events: none;
    user-select: none;
    top: 0;
    left: 0;
    transition: 0.2s ease all;
    -moz-transition: 0.2s ease all;
    -webkit-transition: 0.2s ease all;

    &::after {
      content: url("../../../../assets/icons/icon-map-black.svg");
      width: 40px;
      height: 30px;
      position: absolute;
      left: 0;
      top: 7px;
      text-align: center;
    }
  }
}

.select {
  display: inline-block;
  width: 120px;
  height: 38px;
  line-height: 38px;
  position: relative;
  border-radius: 10px;

  .icon {
    width: 40px;
    height: 40px;
    position: absolute;
    left: 0;
    top: 0;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;

    img {
      padding-top: 8px;
    }

    .img-location {
      padding-left: 10px;
      @media (max-width: 1200px) {
        padding-left: 0;
      }
    }
  }
}

#addresses_select {
  padding: 0 12px 0 32px;
  border-radius: 10px;
  color: #000;
}

.local-center-select {
  justify-content: start;
  @media (max-width: 1200px) {
    justify-content: center;
  }
}

.icon-location {
  width: 60px !important;
  height: 60px !important;

  img {
    padding-left: 20px !important;
    padding-top: 18px !important;
    @media (max-width: 1200px) {
      padding-left: 0 !important;
    }
  }
}
</style>

<style lang="scss">
@import '~theme/css/layout/screen';

.select-wrapper {
  .select {
    &-root {
      .options-list {
        width: calc(100% - 8px);
        transform: translate(-8px, -10%);
        margin: 16px 0 0;

        &.active {
          transform: translate(-8px, 0);
        }

        &__content {
          max-height: 240px;
          overflow: auto;
          scrollbar-width: thin;
        }
      }

      @media only screen and (max-width: 1200px) {
        .options-list {
          min-width: 320px;
        }
      }

      @media only screen and (max-width: $screen-md-l) {
        .options-list {
          left: 32px;
          transform: translate(-24px, -10%);

          &.active {
            transform: translate(-24px, 0);
          }
        }
      }

      @media only screen and (max-width: $screen-sm-l) {
        transform: scale(0.9);

        .options-list {
          min-width: fit-content;
          left: -28px;
          border-top-right-radius: 0;
          border-top-left-radius: 0;
          transform: translate(0, 0);
          margin: 0;

          &.active {
            transform: translate(0, 16px);
          }

          &__content {
            > li {
              width: calc(100vw - 12px);

              @media only screen and (max-width: $screen-sm + 40px) {
                width: calc(100vw - 18px);
              }

              @media only screen and (max-width: $screen-sm) {
                width: calc(100vw - 22px);
              }

              @media only screen and (max-width: $screen-xs-l + 30px) {
                width: calc(100vw - 26px);
              }

              @media only screen and (max-width: $screen-xs-l) {
                width: calc(100vw - 30px);
              }

              @media only screen and (max-width: $screen-xs) {
                width: calc(100vw - 34px);
              }

              @media only screen and (max-width: $screen-xs-s) {
                width: calc(100vw - 40px);
              }
            }
          }
        }
      }
    }

    &-input {
      .base-input {
        input {
          padding-left: 24px;
          border: none;
          background: white;
          padding-right: 36px;
        }

        label {
          width: 1px;
          height: 1px;
          overflow: hidden;
          clip: rect(0 0 0 0);
          white-space: nowrap;
        }

        @media only screen and (max-width: $screen-md) {
          input {
            padding: 0;
            opacity: 0;
          }

          &::after {
            display: none;
          }
        }
      }
    }
  }
}
</style>
